import React from 'react';
import { CollapseFAQ, H3, LayoutSingleColumn, NamedLink, Page } from '../../components';
import SectionFooter from '../PageBuilder/SectionBuilder/SectionFooter';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import { useIntl } from '../../util/reactIntl';

import css from './HouseSittingVisaPage.module.css';
import image1 from './images/IMage - House Sitting Visa Page - charlotte-noelle-98WPMlTl5xo-unsplash.jpg';
import { faqs } from './faqs';

const HouseSittingVisaPage = props => {
    const intl = useIntl();
    const schemaTitle = intl.formatMessage({ id: 'HouseSittingVisaPage.schemaTitle' });
    const schemaDescription = intl.formatMessage({ id: 'HouseSittingVisaPage.schemaDescription' });
    return (
        <Page
            title={schemaTitle}
            description={schemaDescription}
        >
            <LayoutSingleColumn
                topbar={<TopbarContainer />}
                footer={<SectionFooter />}
                mainColumnClassName={css.mainColumnClassName}
            >
                <div className={css.root}>
                    <H3>
                        Visas and Legal Requirements for House Sitting
                    </H3>

                    <p className={css.info}>
                        Considering <NamedLink name="HouseSittingAbroadPage" params={{ location: 'default' }}>house sitting abroad</NamedLink>?
                    </p>
                    <p className={css.info}>
                        If you're new to house sitting, you might think you need a special house sitting visa. Think again. While house sitting is a fantastic way to travel, the documentation required is not a house sitting visa but typically a tourist visa for the country you're visiting.
                    </p>
                    <p className={css.info}>
                        Read on to learn more about the essential travel documents you need and what you should consider before you embark on your house sitting adventure.
                    </p>

                    <div className={css.imageContainer}>
                        <img className={css.image} src={image1} alt="House sitting Visa" />
                        <p className={css.info}>Photo by Charlotte Noelle</p>
                    </div>
                    <br />

                    <p className={css.subtitle}>
                        What Is a House Sitting Visa?
                    </p>
                    <p className={css.info}>
                        Contrary to common belief, there is no such thing as a dedicated “house sitting visa”. While house sitting can offer free accommodation in exchange for your services, you will most often need a tourist visa to legally enter and stay in the country for house sitting purposes.
                    </p>

                    <p className={css.subtitle}>
                        The Reality of House Sitting Abroad
                    </p>

                    <ul className={css.list}>
                        <li>
                            <strong>Short Duration: </strong>
                            <NamedLink name="SearchHouseSitPage">Most house sits</NamedLink> are short-term, typically lasting a few weeks to a couple of months. These durations usually fall under tourist visa requirements, not a specialised house sitting visa.
                        </li>
                        <li>
                            <strong>Research is Key: </strong>
                            Before embarking on a house sitting adventure, research the specific visa requirements for your destination. The rules can vary significantly depending on your nationality and the country you plan to visit.
                        </li>
                    </ul>

                    <p className={css.subtitle}>
                        What to Consider Before Your Trip
                    </p>
                    <ul className={css.list}>
                        <li>
                            <strong>Country-Specific Visa Requirements: </strong>
                            Each country has its own entry requirements. Visit the official government websites or embassies of the countries you are planning to house sit in to check the latest visa information.
                        </li>
                        <li>
                            <strong>Travel Documentation: </strong>
                            You will generally need a valid passport, and sometimes proof of onward travel (such as a return ticket). Ensure all documents are in order before you travel.
                        </li>
                        <li>
                            <strong>Duration of Stay: </strong>
                            Understand the length of time allowed for stays in each country on a tourist visa and plan accordingly.
                        </li>
                        <li>
                            <strong>Work Restrictions: </strong>
                            While house sitting isn't considered employment, some countries may have restrictions about working without a proper work visa. Confirm with local authorities to avoid any issues during your house sit.
                        </li>
                    </ul>

                    <p className={css.subtitle} style={{ marginBottom: 0 }}>
                        FAQ: Common Questions About House Sitting Visas
                    </p>
                    <CollapseFAQ
                        list={faqs}
                    />

                    <p className={css.subtitle}>
                        Helpful Links
                    </p>
                    <ul className={css.list}>
                        <li>General Visa Information for Travel</li>
                        <li>House Sitting Benefits and Tips</li>
                        <li>International House Sitting Opportunities</li>
                    </ul>

                    <p className={css.subtitle}>
                        Always Check Legal Requirements First!
                    </p>
                    <p className={css.info}>
                        Before planning your house sitting adventure, ensure you understand the visa requirements for your destination. While there isn’t a specialised house sitting visa, the necessary documentation for travel can easily be managed with a tourist visa.
                    </p>
                    <p className={css.info}>
                        Happy house sitting!
                    </p>
                </div>
            </LayoutSingleColumn>
        </Page>
    );
};

export default HouseSittingVisaPage;